import React, { useState, useEffect } from 'react';
import ApiService from '../../services/api-service';
import './memorySection.scss';

function MemorySection({ userId, loading, setLoading }) {
  const [memoryInput, setMemoryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [memories, setMemories] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) fetchMemories();
    // eslint-disable-next-line
  }, [userId]);

  const fetchMemories = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ApiService.AI_GET_MEMORIES({
        limit: 20,
        sortBy: 'timestamp',
        sortOrder: 'desc'
      });
      const memoriesData = res?.memories || res?.data?.memories || res?.data || [];
      const normalizedMemories = memoriesData.map(mem => ({
        ...mem,
        pineconeId: mem.pineconeId || mem.id || mem._id,
        content: mem.content,
        timestamp: mem.timestamp,
        source: mem.source,
        importance: mem.importance,
        score: mem.score
      }));
      setMemories(normalizedMemories);
    } catch (e) {
      console.error('[Memory] Failed to fetch memories:', e);
      setMemories([]);
      setError('Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const handleMemorySubmit = async (e) => {
    e.preventDefault();
    if (!memoryInput.trim() || !userId) return;
    setLoading(true);
    setError(null);
    try {
      await ApiService.AI_ADD_MEMORY(memoryInput.trim(), {
        importance: 7,
        source: 'user-input'
      });
      setMemoryInput('');
      fetchMemories();
    } catch (e) {
      console.error('[Memory] Failed to save memory:', e);
      setError('Failed to save memory');
      setLoading(false);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    if (!userId || !memoryId) return;
    setLoading(true);
    setError(null);
    try {
      await ApiService.AI_DELETE_MEMORY(memoryId);
      fetchMemories();
    } catch (e) {
      console.error('[Memory] Failed to delete memory:', e);
      setError('Failed to delete memory');
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchMemories();
      setIsSearchMode(false);
      return;
    }
    setLoading(true);
    setIsSearchMode(true);
    setError(null);
    try {
      const res = await ApiService.AI_SEARCH_MEMORIES(searchQuery, { limit: 10 });
      const searchResults = res?.results || res?.data?.results || [];
      const normalizedResults = searchResults.map(mem => ({
        ...mem,
        pineconeId: mem.pineconeId || mem.id || mem._id,
        content: mem.content,
        timestamp: mem.timestamp,
        source: mem.source,
        importance: mem.importance,
        score: mem.score
      }));
      setMemories(normalizedResults);
    } catch (e) {
      console.error('[Memory] Failed to search memories:', e);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetMemories = async () => {
    if (!window.confirm('Are you sure you want to delete ALL memories? This cannot be undone.')) return;
    setLoading(true);
    setError(null);
    try {
      await ApiService.AI_RESET_MEMORIES();
      setMemories([]);
    } catch (e) {
      console.error('[Memory] Failed to reset memories:', e);
      setError('Failed to reset memories');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    fetchMemories();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="memory-section">
      {/* Header */}
      <div className="memory-header">
        <h3>AI Memory</h3>
        <button 
          onClick={handleResetMemories} 
          className="btn btn-danger"
          disabled={loading}
        >
          <span className="btn-text-full">Reset All</span>
          <span className="btn-text-short">Reset</span>
        </button>
      </div>

      {/* Save Memory Card */}
      <div className="memory-card">
        <div className="form-label">Save to Memory</div>
        <form onSubmit={handleMemorySubmit} className="memory-form-vertical">
          <textarea
            value={memoryInput}
            onChange={(e) => setMemoryInput(e.target.value)}
            placeholder="Type something to remember..."
            disabled={loading}
            className="memory-textarea"
            rows={4}
          />
          <button
            type="submit"
            disabled={loading || !memoryInput.trim() || !userId}
            className="btn btn-primary"
          >
            Save
          </button>
        </form>
      </div>

      {/* Search Card */}
      <div className="memory-card memory-card-search">
        <form onSubmit={handleSearch} className="memory-form">
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search memories..."
            disabled={loading}
            className="memory-input"
          />
          <button
            type="submit"
            disabled={loading || !userId}
            className="btn btn-secondary"
          >
            Search
          </button>
          {isSearchMode && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="btn btn-clear"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Error Message */}
      {error && <div className="memory-error">{error}</div>}

      {/* Content Area */}
      {loading ? (
        <div className="memory-loading">Loading...</div>
      ) : (
        <>
          {memories.length === 0 ? (
            <div className="memory-empty">
              {isSearchMode ? 'No memories match your search' : 'No memories found'}
            </div>
          ) : (
            <div className="memory-list-container">
              <ul className="memory-list">
                {memories.map((mem) => (
                  <li 
                    key={mem.pineconeId || mem._id} 
                    className="memory-item"
                  >
                    <div className="memory-content">
                      <div className="memory-text-container">
                        <p className="memory-text">{mem.content}</p>
                        <div className="memory-meta">
                          <span className="memory-date">
                            {formatDate(mem.timestamp)}
                          </span>
                          {mem.score !== undefined && (
                            <span className="memory-relevance">
                              Relevance: {Math.round(mem.score * 100)}%
                            </span>
                          )}
                          {mem.source && (
                            <span className="memory-source">
                              Source: {mem.source}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteMemory(mem.pineconeId || mem._id)}
                        className="memory-delete"
                        title="Delete"
                        disabled={loading}
                        aria-label="Delete memory"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MemorySection;